import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Search, AlertOctagon, AlertTriangle, Info, ChevronRight, Check, X,
  Users, Truck, FileText, Wallet, Building2, Inbox,
} from "lucide-react";

// ============================================================
// STATUS BADGE — more compact, clearer semantics
// ============================================================
export function StatusBadge({ status, size = "sm" }) {
  const s = (status || "").toLowerCase();
  const map = {
    aktiv: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    ok: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    freigegeben: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    bezahlt: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    behoben: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    erledigt: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    abgeschlossen: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    ausgeliefert: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    abgerechnet: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    geprueft: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    gesendet: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    bestätigt: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    urlaub: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    in_bearbeitung: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    offen: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    bald: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    entwurf: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    warnung: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    werkstatt: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    krank: "bg-red-500/10 text-red-400 border-red-500/20",
    abgelaufen: "bg-red-500/10 text-red-400 border-red-500/20",
    kritisch: "bg-red-500/10 text-red-400 border-red-500/20",
    überfällig: "bg-red-500/10 text-red-400 border-red-500/20",
    storniert: "bg-red-500/10 text-red-400 border-red-500/20",
    strafe: "bg-red-500/10 text-red-400 border-red-500/20",
    abgelehnt: "bg-red-500/10 text-red-400 border-red-500/20",
    abzug: "bg-red-500/10 text-red-400 border-red-500/20",
    bonus: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    inaktiv: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
  };
  const cls = map[s] || "bg-zinc-500/10 text-zinc-400 border-zinc-500/20";
  const sz = size === "md"
    ? "px-2.5 py-1 text-[11px]"
    : "px-2 py-0.5 text-[10px]";
  return (
    <span className={`inline-flex items-center font-semibold uppercase tracking-wider border rounded ${cls} ${sz}`}>
      {status || "—"}
    </span>
  );
}

// ============================================================
// KPI CARD (unchanged API)
// ============================================================
export function KpiCard({ label, value, hint, tone = "default", testid, icon: Icon, onClick }) {
  const tones = {
    default: "text-zinc-100",
    success: "text-emerald-400",
    warning: "text-amber-400",
    critical: "text-red-400",
  };
  return (
    <div
      onClick={onClick}
      className={`bg-zinc-900 border border-zinc-800 rounded-md p-5 hover:border-zinc-700 transition-colors ${onClick ? "cursor-pointer" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-zinc-600" />}
      </div>
      <div className={`kpi-value mt-3 tabular-nums ${tones[tone]}`}>{value}</div>
      {hint && <div className="mt-2 text-xs text-zinc-500">{hint}</div>}
    </div>
  );
}

// ============================================================
// SECTION HEADER (unchanged)
// ============================================================
export function SectionHeader({ title, subtitle, actions }) {
  return (
    <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-zinc-50">{title}</h1>
        {subtitle && <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2">{actions}</div>
    </div>
  );
}

// ============================================================
// EMPTY STATE
// ============================================================
/**
 * Phase I — Konsistente Empty-States über alle Listen-Pages.
 *
 * Akzeptiert mehrere prop-Aliase rückwärts-kompatibel zu allem, was
 * vor dem Konsolidieren existierte:
 *   • `title` / `label` / `text`  → Hauptzeile
 *   • `icon`                       → optionales lucide-Icon (Default: Inbox)
 *   • `hint`                       → kleine Unterzeile
 *   • `cta`                        → optionales Action-Element (Button/Link)
 *
 * Visuals: zinc-900 Card mit Icon (zinc-600), Title in zinc-300, Hint
 * in zinc-500. data-testid="empty-state" für Tests.
 */
export function EmptyState({ title, label, text, hint, icon: Icon = Inbox, cta, className = "", testid = "empty-state" }) {
  const heading = title || label || text || "Keine Daten vorhanden";
  return (
    <div
      data-testid={testid}
      className={`bg-zinc-900 border border-zinc-800 rounded p-8 flex flex-col items-center justify-center text-center ${className}`}
    >
      <Icon className="w-8 h-8 text-zinc-600 mb-3" strokeWidth={1.5} />
      <div className="text-sm text-zinc-300 font-medium">{heading}</div>
      {hint && <div className="text-xs text-zinc-500 mt-1.5">{hint}</div>}
      {cta && <div className="mt-4">{cta}</div>}
    </div>
  );
}

// ============================================================
// ALERT CARD (critical / warning / info)
// ============================================================
export function AlertCard({ tone = "info", title, count, description, onClick, testid }) {
  const tones = {
    critical: {
      border: "border-red-500/30",
      bg: "bg-red-500/5 hover:bg-red-500/10",
      icon: AlertOctagon,
      iconColor: "text-red-400",
      countColor: "text-red-400",
      label: "KRITISCH",
      labelColor: "text-red-400 bg-red-500/10 border-red-500/30",
    },
    warning: {
      border: "border-amber-500/30",
      bg: "bg-amber-500/5 hover:bg-amber-500/10",
      icon: AlertTriangle,
      iconColor: "text-amber-400",
      countColor: "text-amber-400",
      label: "WARNUNG",
      labelColor: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    },
    info: {
      border: "border-blue-500/30",
      bg: "bg-blue-500/5 hover:bg-blue-500/10",
      icon: Info,
      iconColor: "text-blue-400",
      countColor: "text-blue-400",
      label: "INFO",
      labelColor: "text-blue-400 bg-blue-500/10 border-blue-500/30",
    },
  };
  const t = tones[tone];
  const Icon = t.icon;
  return (
    <div
      onClick={onClick}
      data-testid={testid}
      className={`border rounded-md p-4 transition-colors ${t.border} ${t.bg} ${onClick ? "cursor-pointer" : ""}`}
    >
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 ${t.iconColor} shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-1.5 py-0.5 text-[9px] tracking-[0.15em] font-bold border rounded ${t.labelColor}`}>{t.label}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className={`text-2xl font-mono font-bold tabular-nums ${t.countColor}`}>{count}</span>
            <span className="text-sm font-medium text-zinc-100 truncate">{title}</span>
          </div>
          {description && <p className="text-xs text-zinc-400 mt-1">{description}</p>}
        </div>
        {onClick && <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0 mt-1" />}
      </div>
    </div>
  );
}

// ============================================================
// BREADCRUMBS
// ============================================================
const ROUTE_LABELS = {
  "": "Kommandozentrale",
  "operations": "Operations",
  "heute": "Heute",
  "zeiterfassung": "Zeiterfassung",
  "lenkprotokoll": "Lenkprotokoll",
  "dienstplan": "Dienstplan",
  "aufgaben": "Aufgaben",
  "mitarbeiter": "Mitarbeiter",
  "fuhrpark": "Fuhrpark",
  "dokumente": "Dokumente",
  "finanzen": "Finanzen",
  "export": "Export",
  "audit": "Audit",
  "einstellungen": "Einstellungen",
};

export function Breadcrumbs() {
  const loc = useLocation();
  const parts = loc.pathname.split("/").filter(Boolean);

  if (parts.length === 0) {
    return <span className="text-sm text-zinc-300 font-medium" data-testid="breadcrumbs">Kommandozentrale</span>;
  }

  const crumbs = [];
  crumbs.push({ href: "/", label: "Kommandozentrale" });
  let acc = "";
  for (const p of parts) {
    acc += "/" + p;
    const isId = /^[0-9a-f]{8}-/i.test(p); // UUID-ish
    const label = isId ? "Details" : (ROUTE_LABELS[p] || p);
    crumbs.push({ href: acc, label });
  }

  return (
    <nav className="flex items-center gap-1.5 text-xs" data-testid="breadcrumbs">
      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <span key={c.href} className="flex items-center gap-1.5">
            {i > 0 && <ChevronRight className="w-3 h-3 text-zinc-600" />}
            {isLast ? (
              <span className="text-zinc-200 font-medium">{c.label}</span>
            ) : (
              <Link to={c.href} className="text-zinc-500 hover:text-zinc-300 transition-colors">{c.label}</Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}

// ============================================================
// GLOBAL SEARCH — ⌘K dialog across employees/vehicles/customers/invoices
// ============================================================
export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ employees: [], vehicles: [], customers: [], invoices: [], departments: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [allDepts, setAllDepts] = useState([]);

  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
    if (open && allDepts.length === 0) {
      api.get("/departments").then(({ data }) => setAllDepts(data)).catch(() => {});
    }
  }, [open, allDepts.length]);

  useEffect(() => {
    if (!open || !q || q.length < 2) {
      setResults({ employees: [], vehicles: [], customers: [], invoices: [], departments: [] });
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const [e, v, c, i] = await Promise.all([
          api.get("/employees", { params: { q } }),
          api.get("/vehicles", { params: { q } }),
          api.get("/customers", { params: { q } }),
          api.get("/invoices", { params: { q } }),
        ]);
        const qlc = q.toLowerCase();
        const deptMatches = allDepts.filter((d) => (d.name || "").toLowerCase().includes(qlc)).slice(0, 5);
        setResults({
          employees: e.data.slice(0, 5),
          vehicles: v.data.slice(0, 5),
          customers: c.data.slice(0, 5),
          invoices: i.data.slice(0, 5),
          departments: deptMatches,
        });
      } catch { /* silent */ }
      setLoading(false);
    }, 200);
    return () => clearTimeout(t);
  }, [q, open, allDepts]);

  function go(path) {
    setOpen(false);
    setQ("");
    navigate(path);
  }

  const total = results.employees.length + results.vehicles.length + results.customers.length + results.invoices.length + results.departments.length;

  return (
    <>
      <button
        data-testid="global-search-trigger"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded text-zinc-400 hover:text-zinc-200 transition-colors min-w-[220px]"
      >
        <Search className="w-3.5 h-3.5" />
        <span>Suchen…</span>
        <span className="ml-auto font-mono text-[10px] px-1.5 py-0.5 bg-zinc-800 rounded text-zinc-500">⌘K</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-black/70 flex items-start justify-center pt-24 z-50"
          onClick={() => setOpen(false)}
          data-testid="global-search-dialog"
        >
          <div
            className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-zinc-800 px-4 py-3 flex items-center gap-3">
              <Search className="w-4 h-4 text-zinc-500" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Abteilung, Mitarbeiter, Fahrzeug, Kunde, Rechnung…"
                data-testid="global-search-input"
                className="flex-1 bg-transparent text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none"
              />
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-300"><X className="w-4 h-4"/></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {q.length < 2 && (
                <div className="p-8 text-center text-sm text-zinc-500">
                  Tippe mindestens 2 Zeichen, um zu suchen.
                </div>
              )}
              {q.length >= 2 && loading && (
                <div className="p-4 text-center text-xs text-zinc-500">Suche…</div>
              )}
              {q.length >= 2 && !loading && total === 0 && (
                <div className="p-8 text-center text-sm text-zinc-500">Keine Treffer für "{q}".</div>
              )}
              {results.departments.length > 0 && (
                <SearchGroup label="Abteilungen" icon={Building2}>
                  {results.departments.map((d) => (
                    <SearchItem key={d.id} testid={`gs-dept-${d.id}`}
                      onClick={() => go(`/mitarbeiter?dept=${d.id}`)}
                      title={d.name}
                      subtitle="Abteilung · Mitarbeiter anzeigen"/>
                  ))}
                </SearchGroup>
              )}
              {results.employees.length > 0 && (
                <SearchGroup label="Mitarbeiter" icon={Users}>
                  {results.employees.map((e) => (
                    <SearchItem key={e.id} testid={`gs-emp-${e.id}`} onClick={() => go(`/mitarbeiter/${e.id}`)}
                      title={`${e.first_name} ${e.last_name}`} subtitle={`${e.role} · ${e.department || "—"}`} status={e.status}/>
                  ))}
                </SearchGroup>
              )}
              {results.vehicles.length > 0 && (
                <SearchGroup label="Fahrzeuge" icon={Truck}>
                  {results.vehicles.map((v) => (
                    <SearchItem key={v.id} testid={`gs-veh-${v.id}`} onClick={() => go(`/fuhrpark/${v.id}`)}
                      title={v.license_plate} subtitle={`${v.brand} ${v.model} · ${v.department || "—"}`} status={v.status} mono/>
                  ))}
                </SearchGroup>
              )}
              {results.customers.length > 0 && (
                <SearchGroup label="Kunden" icon={Wallet}>
                  {results.customers.map((c) => (
                    <SearchItem key={c.id} testid={`gs-cust-${c.id}`} onClick={() => go(`/finanzen`)}
                      title={c.company_name} subtitle={c.uid_number || c.email || "—"}/>
                  ))}
                </SearchGroup>
              )}
              {results.invoices.length > 0 && (
                <SearchGroup label="Rechnungen" icon={FileText}>
                  {results.invoices.map((i) => (
                    <SearchItem key={i.id} testid={`gs-inv-${i.id}`} onClick={() => go(`/finanzen`)}
                      title={i.number} subtitle={i.description?.slice(0, 50)} status={i.status} mono/>
                  ))}
                </SearchGroup>
              )}
            </div>
            <div className="border-t border-zinc-800 px-4 py-2 flex items-center justify-between text-[10px] text-zinc-500">
              <span>Mit Enter öffnen</span>
              <span className="font-mono">ESC zum Schließen</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SearchGroup({ label, icon: Icon, children }) {
  return (
    <div className="py-1">
      <div className="px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1.5 bg-zinc-900/50">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

function SearchItem({ onClick, title, subtitle, status, testid, mono }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-zinc-800/60 transition-colors"
    >
      <div className="flex-1 min-w-0">
        <div className={`text-sm text-zinc-100 truncate ${mono ? "font-mono" : "font-medium"}`}>{title}</div>
        {subtitle && <div className="text-xs text-zinc-500 truncate">{subtitle}</div>}
      </div>
      {status && <StatusBadge status={status} />}
      <ChevronRight className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
    </button>
  );
}

// ============================================================
// FORM SECTION — groups fields under a labeled header
// ============================================================
export function FormSection({ title, description, children }) {
  return (
    <div className="border-b border-zinc-800 last:border-b-0 py-5 first:pt-0 last:pb-0">
      <div className="mb-3">
        <h4 className="text-sm font-semibold text-zinc-100">{title}</h4>
        {description && <p className="text-xs text-zinc-500 mt-0.5">{description}</p>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

// ============================================================
// STEPPER — for wizard-style create forms
// ============================================================
export function Stepper({ steps, current, onStepClick }) {
  return (
    <div className="flex items-center gap-2" data-testid="stepper">
      {steps.map((s, i) => {
        const isActive = i === current;
        const isDone = i < current;
        const clickable = onStepClick && i <= current;
        return (
          <div key={s} className="flex items-center gap-2 flex-1">
            <button
              onClick={() => clickable && onStepClick(i)}
              disabled={!clickable}
              data-testid={`stepper-step-${i}`}
              className={`flex items-center gap-2 ${clickable ? "cursor-pointer" : "cursor-default"}`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold border transition-colors ${
                isActive ? "bg-blue-600 border-blue-500 text-white" :
                isDone ? "bg-emerald-600/20 border-emerald-500/40 text-emerald-400" :
                "bg-zinc-900 border-zinc-700 text-zinc-500"
              }`}>
                {isDone ? <Check className="w-3 h-3" /> : i + 1}
              </div>
              <span className={`text-xs font-medium ${
                isActive ? "text-zinc-100" : isDone ? "text-zinc-300" : "text-zinc-500"
              }`}>{s}</span>
            </button>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-px ${isDone ? "bg-emerald-500/40" : "bg-zinc-800"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
// FIELD — consistent labeled input wrapper
// ============================================================
export function Field({ label, required, children, colSpan }) {
  return (
    <div className={colSpan === 2 ? "sm:col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
    </div>
  );
}

export const inputCls = "w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:ring-2 focus:ring-blue-600 focus:outline-none";
